import React from "react";
import CorporateHeader from "../../../../Corporate/components/CorporateHeader";
import StepperPills from "../../../../Corporate/components/StepperPills";
import HeroImage from "../../../../Corporate/Activation/sections/HeroImage";
import Hero from "../../../../Corporate/Activation/sections/Hero";
import WhatThisIs from "../../../../Corporate/Activation/sections/WhatThisIs";
import WhenToUse from "../../../../Corporate/Activation/sections/WhenToUse";
import WhereItFits from "../../../../Corporate/Activation/sections/WhereItFits";
import PrivateByDesign from "../../../../Corporate/Activation/sections/PrivateByDesign";
import ClosingTagline from "../../../../Corporate/Activation/sections/ClosingTagline";
import Footer from "../../../../Corporate/Activation/sections/Footer";
import EmptySectionStub from "./EmptySectionStub";
import "../../../../Corporate/Activation/style.css";

// Detect whether a section's content is "blank" (would cause the section
// component to render null on the public page). When blank, the admin
// preview swaps in a clickable EmptySectionStub so every slot stays visible
// and editable.
const isBlank = (key, c) => {
  if (!c || typeof c !== "object") return true;
  switch (key) {
    case "hero_image":
      return !c.image_s3_key && !c.image_url;
    case "hero":
      return !c.headline?.trim() && !c.subtext?.trim();
    case "what_this_is":
      return !c.description?.trim() && (c.pill_chips?.length || 0) === 0;
    case "when_to_use":
      return !c.description?.trim() && (c.items?.length || 0) === 0;
    case "where_it_fits":
      return !c.description?.trim() && (c.cards?.length || 0) === 0;
    case "private_by_design":
      return !c.description?.trim() && (c.bullets?.length || 0) === 0;
    case "closing_tagline":
      return !c.text?.trim();
    case "footer":
      return !c.sponsor_line?.trim() && !c.copyright_line?.trim();
    case "cta":
      return !c.card_heading?.trim() && !c.button_label?.trim();
    case "step_labels":
      return (c.labels?.length || 0) === 0;
    default:
      return true;
  }
};

const COMPONENT_BY_KEY = {
  hero: Hero,
  what_this_is: WhatThisIs,
  when_to_use: WhenToUse,
  where_it_fits: WhereItFits,
  private_by_design: PrivateByDesign,
  closing_tagline: ClosingTagline,
};

// Default order for the reorderable middle column. Used when no explicit
// `sectionOrder` prop is provided.
const DEFAULT_MIDDLE_ORDER = [
  "hero",
  "what_this_is",
  "when_to_use",
  "where_it_fits",
  "private_by_design",
  "closing_tagline",
];

// Sections that render a leading "01"/"02"/... badge. The badge number is
// driven by the rendered position so reordering keeps numbers contiguous.
const NUMBERED_SECTION_KEYS = new Set([
  "what_this_is",
  "when_to_use",
  "where_it_fits",
  "private_by_design",
]);

const PreviewLayout = ({
  branding = {},
  sections = {},
  renderSection,
  sectionOrder,
  hiddenKeys,
}) => {
  const themeStyle = {
    "--corporate-primary": branding.primary_color || "#4377fc",
    "--corporate-secondary": branding.secondary_color || "#0d5eaf",
    "--corporate-font-scale":
      typeof branding.content_font_scale === "number"
        ? branding.content_font_scale
        : 1,
  };

  const hiddenSet = new Set(hiddenKeys || []);
  const middleOrder = (() => {
    if (!sectionOrder || sectionOrder.length === 0) return DEFAULT_MIDDLE_ORDER;
    const reorderable = sectionOrder.filter((k) => COMPONENT_BY_KEY[k]);
    // Add any registry-known middle sections missing from the override so
    // newly added sections still show up.
    DEFAULT_MIDDLE_ORDER.forEach((k) => {
      if (!reorderable.includes(k)) reorderable.push(k);
    });
    return reorderable;
  })();

  // Render the real section component when there's content, otherwise an
  // empty stub. Both are wrapped in the click-to-edit hotspot via wrap().
  const renderOrStub = (key, RealComponent, number) => {
    const content = sections[key];
    if (isBlank(key, content)) {
      return <EmptySectionStub sectionKey={key} />;
    }
    return <RealComponent section={content} number={number} />;
  };

  const wrap = (key, node) =>
    renderSection ? renderSection(key, node) : node;

  const cta = sections.cta || {};
  const stepLabels = sections.step_labels?.labels?.length
    ? sections.step_labels.labels
    : ["Verify", "Set up", "Start using"];

  return (
    <div
      className="corp-activation rounded-lg overflow-hidden border"
      style={themeStyle}
    >
      <CorporateHeader branding={branding} />

      <div className="max-w-[1440px] mx-auto px-4 md:px-9 pt-8 md:pt-10 pb-6">
        {!hiddenSet.has("hero_image") && (
          <div className="mb-8">
            {wrap(
              "hero_image",
              isBlank("hero_image", sections.hero_image) ? (
                <EmptySectionStub sectionKey="hero_image" />
              ) : (
                <HeroImage section={sections.hero_image} />
              )
            )}
          </div>
        )}
        <div className="flex flex-col-reverse lg:flex-row lg:items-start gap-8">
          <div className="flex flex-col gap-8 flex-1 max-w-full lg:max-w-[680px]">
            {(() => {
              let numberedCounter = 0;
              return middleOrder.map((key) => {
                if (hiddenSet.has(key)) return null;
                const Component = COMPONENT_BY_KEY[key];
                if (!Component) return null;
                const number = NUMBERED_SECTION_KEYS.has(key)
                  ? String(++numberedCounter).padStart(2, "0")
                  : undefined;
                return (
                  <React.Fragment key={key}>
                    {wrap(key, renderOrStub(key, Component, number))}
                  </React.Fragment>
                );
              });
            })()}
          </div>

          <div className="w-full lg:w-[420px] lg:flex-shrink-0">
            <div className="corp-cta-card-shadow">
              {wrap(
                "cta",
                <div className="corp-cta-card-header">
                  <div className="flex flex-col gap-2">
                    <h2 className="text-xl font-semibold text-white m-0">
                      {cta.card_heading || "Activate Your Access"}
                    </h2>
                    {cta.card_subtitle && (
                      <p className="text-xs text-white/90 leading-relaxed m-0">
                        {cta.card_subtitle}
                      </p>
                    )}
                  </div>
                  {wrap(
                    "step_labels",
                    <StepperPills labels={stepLabels} activeIndex={0} />
                  )}
                </div>
              )}
              <div className="corp-cta-card-body">
                <div className="flex flex-col gap-7">
                  <div className="flex gap-4">
                    <FormStub label="First Name" />
                    <FormStub label="Last Name" />
                  </div>
                  <FormStub label="Work Email" />
                  <div className="flex gap-4">
                    <FormStub label="Personal Email" />
                    <FormStub label="Confirm Personal Email" />
                  </div>
                  <button
                    type="button"
                    className="corp-primary-button mt-2"
                    disabled
                  >
                    {cta.button_label || "Activate Access"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {wrap(
          "footer",
          isBlank("footer", sections.footer) ? (
            <EmptySectionStub sectionKey="footer" />
          ) : (
            <Footer section={sections.footer} />
          )
        )}
      </div>
    </div>
  );
};

const FormStub = ({ label }) => (
  <div className="flex flex-col gap-2 flex-1 min-w-0">
    <span className="text-base font-semibold text-black">{label}</span>
    <div
      className="h-12 rounded-xl border border-solid border-[#d0d5dd] bg-white"
      style={{ boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)" }}
    />
  </div>
);

export default PreviewLayout;
